import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import {
  Search,
  Building2,
  Users as UsersIcon,
  Store as StoreIcon,
  Crown,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle,
  Calendar,
  Mail,
  ShoppingBag,
  UserCircle,
  Shield,
  UserPlus,
  Loader2,
  Edit2,
} from 'lucide-react';
import { format } from 'date-fns';

// ─── Types ───────────────────────────────────────────────────────────────────

type BillingPlan = 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';
type AccountStatus = 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
type UserRole = 'CUSTOMER' | 'MERCHANT' | 'OWNER' | 'ADMIN' | 'AFFILIATE';
type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
type StoreStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

interface Organisation {
  id: string;
  name: string;
  email: string;
  ownerName?: string;
  ownerEmail?: string;
  plan: BillingPlan;
  status: AccountStatus;
  storeCount: number;
  joinedAt: string;
}

interface OrgMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  plan?: string;
  emailVerified: boolean;
  createdAt: string;
  store?: { id: string; name: string } | null;
  _count?: { ownedStores: number };
}

interface Store {
  id: string;
  name: string;
  subdomain: string;
  status: StoreStatus;
  plan?: string;
  createdAt: string;
  owner?: { firstName: string; lastName: string; email: string } | null;
  _count?: { products: number; orders: number };
}

// ─── Plan badge helper ────────────────────────────────────────────────────────

const planColors: Record<BillingPlan, string> = {
  BASIC: 'bg-gray-100 text-gray-700 border-gray-200',
  PRO: 'bg-blue-50 text-blue-800 border-blue-200',
  MAX: 'bg-purple-50 text-purple-800 border-purple-200',
  ENTERPRISE: 'bg-amber-50 text-amber-800 border-amber-200',
};

const ALL_PLANS: BillingPlan[] = ['BASIC', 'PRO', 'MAX', 'ENTERPRISE'];

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'organisations' | 'users' | 'stores';

// ─────────────────────────────────────────────────────────────────────────────
// ORGANISATIONS TAB
// ─────────────────────────────────────────────────────────────────────────────

function OrganisationsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('ALL');
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
  const [newPlan, setNewPlan] = useState<BillingPlan>('BASIC');

  const { data, isLoading } = useQuery<{ items: Organisation[]; meta: OrgMeta }>({
    queryKey: ['admin-orgs', search, planFilter],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (search) params.append('search', search);
      return apiClient.get(`/admin/billing/tenants?${params}`);
    },
  });

  const orgs = data?.items || [];

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: BillingPlan }) =>
      apiClient.patch(`/admin/billing/tenants/${id}/plan`, { plan }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
      setEditingOrg(null);
    },
  });

  const filtered = orgs.filter((o) => planFilter === 'ALL' || o.plan === planFilter);

  // Stats
  const total = data?.meta?.total ?? orgs.length;
  const planCounts = orgs.reduce(
    (acc, o) => {
      acc[o.plan] = (acc[o.plan] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div className="space-y-6">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-1">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total</p>
          <p className="text-3xl font-black text-black">{total}</p>
        </div>
        {ALL_PLANS.map((plan) => (
          <div key={plan} className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-1">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{plan}</p>
            <p className="text-3xl font-black text-black">{planCounts[plan] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-xl border-2 border-gray-200 bg-white px-4 pl-10 py-2 text-sm focus:outline-none focus:border-black transition-colors"
          />
        </div>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="h-10 rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-medium text-black focus:border-black focus:outline-none"
        >
          <option value="ALL">All Plans</option>
          {ALL_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-black animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-16 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-black text-black">No organisations found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Organisation</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Billing Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stores</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-black text-sm">{org.name}</p>
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                            <span>#{org.id.slice(0, 8)}</span>
                            {org.ownerName && <span>• Owner: {org.ownerName}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-gray-700 text-sm truncate">{org.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold uppercase border ${planColors[org.plan]}`}>
                        <Crown className="w-3 h-3" />
                        {org.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <StoreIcon className="w-3.5 h-3.5 text-gray-400" />
                        <span className="font-semibold text-black">{org.storeCount}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-gray-600 text-sm whitespace-nowrap">
                          {format(new Date(org.joinedAt), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => { setEditingOrg(org); setNewPlan(org.plan); }}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border-2 border-black text-black text-xs font-bold hover:bg-black hover:text-white transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Change Plan
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {editingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 space-y-5">
            <div>
              <h2 className="text-lg font-black text-black">Change Plan</h2>
              <p className="text-sm text-gray-500 mt-1">
                Organisation: <span className="font-semibold text-black">{editingOrg.name}</span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ALL_PLANS.map((plan) => (
                <button
                  key={plan}
                  onClick={() => setNewPlan(plan)}
                  className={`p-3 rounded-xl border-2 text-sm font-bold transition-all ${
                    newPlan === plan
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <Crown className="w-4 h-4 mx-auto mb-1" />
                  {plan}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setEditingOrg(null)}
                className="flex-1 h-10 rounded-xl border-2 border-gray-200 text-gray-700 text-sm font-bold hover:border-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => updatePlanMutation.mutate({ id: editingOrg.id, plan: newPlan })}
                disabled={updatePlanMutation.isPending || newPlan === editingOrg.plan}
                className="flex-1 h-10 rounded-xl bg-black text-white text-sm font-bold hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {updatePlanMutation.isPending ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// USERS TAB
// ─────────────────────────────────────────────────────────────────────────────

const statusIcons: Record<UserStatus, any> = {
  ACTIVE: CheckCircle,
  INACTIVE: AlertCircle,
  SUSPENDED: XCircle,
};

const roleIcons: Record<UserRole, any> = {
  ADMIN: Shield,
  MERCHANT: ShoppingBag,
  OWNER: ShoppingBag,
  CUSTOMER: UserCircle,
  AFFILIATE: UserPlus,
};

function UsersTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { data: rawUsers = [], error } = useQuery<User[]>({
    queryKey: ['users', roleFilter, statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (roleFilter !== 'ALL') params.append('role', roleFilter);
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);
      return apiClient.get<User[]>(`/users?${params}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: UserStatus }) =>
      apiClient.patch(`/users/${userId}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const users: User[] = (rawUsers as any)?.users || (rawUsers as any)?.data || (Array.isArray(rawUsers) ? rawUsers : []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm font-medium">
        Failed to load users. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: users.length },
          { label: 'Active', value: users.filter((u) => u.status === 'ACTIVE').length },
          { label: 'Merchants', value: users.filter((u) => u.role === 'MERCHANT' || u.role === 'OWNER').length },
          { label: 'Suspended', value: users.filter((u) => u.status === 'SUSPENDED').length },
        ].map((stat) => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
            <p className="text-3xl font-black text-black mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-xl border-2 border-gray-200 bg-white px-4 pl-10 text-sm focus:outline-none focus:border-black transition-colors"
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="h-10 rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-medium text-black focus:border-black focus:outline-none">
          <option value="ALL">All Roles</option>
          <option value="ADMIN">Admin</option>
          <option value="MERCHANT">Merchant</option>
          <option value="CUSTOMER">Customer</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-medium text-black focus:border-black focus:outline-none">
          <option value="ALL">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
      </div>

      {/* Table */}
      {users.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-16 text-center">
          <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-black text-black">No users found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stores</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => {
                  const StatusIcon = statusIcons[user.status] || AlertCircle;
                  const RoleIcon = roleIcons[user.role] || UserCircle;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                            <RoleIcon className="w-4 h-4 text-black" />
                          </div>
                          <div>
                            <Link to={`/users/${user.id}`} className="font-semibold text-black hover:underline text-sm block">
                              {user.firstName} {user.lastName}
                            </Link>
                            <p className="text-[11px] text-gray-400">#{user.id.slice(0, 6)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="text-gray-700 truncate">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold uppercase ${
                          user.role === 'ADMIN' ? 'bg-black text-white' :
                          user.role === 'MERCHANT' || user.role === 'OWNER' ? 'bg-gray-800 text-white' :
                          'bg-gray-100 text-gray-700'}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold uppercase ${
                          user.status === 'ACTIVE' ? 'bg-black text-white' :
                          user.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                          'bg-gray-100 text-gray-700'}`}>
                          <StatusIcon className="w-3 h-3" />
                          {user.status}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {user._count && user._count.ownedStores > 0 ? (
                          <div className="flex items-center gap-1">
                            <StoreIcon className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-semibold text-black">{user._count.ownedStores}</span>
                          </div>
                        ) : user.store ? (
                          <span className="text-sm text-gray-700 truncate">{user.store.name}</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="text-gray-600 whitespace-nowrap">{format(new Date(user.createdAt), 'MMM d, yy')}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/users/${user.id}`}
                            className="h-8 px-3 rounded-lg border-2 border-black text-black text-xs font-bold hover:bg-black hover:text-white transition-colors inline-flex items-center">
                            View
                          </Link>
                          {user.role !== 'ADMIN' && (
                            <button
                              onClick={() => updateStatusMutation.mutate({
                                userId: user.id,
                                status: user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                              })}
                              disabled={updateStatusMutation.isPending}
                              className={`h-8 px-3 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                                user.status === 'ACTIVE' ? 'bg-gray-700 text-white hover:bg-black' : 'bg-black text-white hover:bg-gray-800'}`}>
                              {user.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STORES TAB
// ─────────────────────────────────────────────────────────────────────────────

function StoresTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { data: rawStores, isLoading, error } = useQuery({
    queryKey: ['stores', statusFilter, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (search) params.append('search', search);
      return apiClient.get(`/stores?${params}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ storeId, status }: { storeId: string; status: StoreStatus }) =>
      apiClient.patch(`/stores/${storeId}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stores'] }),
  });

  const stores: Store[] = (rawStores as any)?.data || (Array.isArray(rawStores) ? rawStores : []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm font-medium">
        Failed to load stores. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: 'Total Stores', value: stores.length },
          { label: 'Active', value: stores.filter((s) => s.status === 'ACTIVE').length },
          { label: 'Suspended', value: stores.filter((s) => s.status === 'SUSPENDED').length },
        ].map((stat) => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
            <p className="text-3xl font-black text-black mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or subdomain..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-xl border-2 border-gray-200 bg-white px-4 pl-10 text-sm focus:outline-none focus:border-black transition-colors"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-medium text-black focus:border-black focus:outline-none">
          <option value="ALL">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-black animate-spin" />
        </div>
      ) : stores.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-16 text-center">
          <StoreIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-black text-black">No stores found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Store</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stores.map((store) => (
                  <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                          <StoreIcon className="w-4 h-4 text-black" />
                        </div>
                        <div>
                          <Link to={`/stores/${store.id}`} className="font-semibold text-black hover:underline text-sm block">
                            {store.name}
                          </Link>
                          <p className="text-[11px] text-gray-400">{store.subdomain}.dukene.com</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {store.owner ? (
                        <div>
                          <p className="font-medium text-black text-sm">{store.owner.firstName} {store.owner.lastName}</p>
                          <p className="text-[11px] text-gray-400">{store.owner.email}</p>
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {store.plan ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold uppercase border ${planColors[store.plan as BillingPlan] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          <Crown className="w-3 h-3" />
                          {store.plan}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold uppercase ${
                        store.status === 'ACTIVE' ? 'bg-black text-white' :
                        store.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                        'bg-gray-100 text-gray-700'}`}>
                        {store.status === 'ACTIVE' ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                        {store.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-gray-600 whitespace-nowrap">{format(new Date(store.createdAt), 'MMM d, yyyy')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/stores/${store.id}`}
                          className="h-8 px-3 rounded-lg border-2 border-black text-black text-xs font-bold hover:bg-black hover:text-white transition-colors inline-flex items-center">
                          View
                        </Link>
                        <button
                          onClick={() => updateStatusMutation.mutate({
                            storeId: store.id,
                            status: store.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                          })}
                          disabled={updateStatusMutation.isPending}
                          className={`h-8 px-3 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                            store.status === 'ACTIVE' ? 'bg-gray-700 text-white hover:bg-black' : 'bg-black text-white hover:bg-gray-800'}`}>
                          {store.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; icon: any }[] = [
  { id: 'organisations', label: 'Organisations', icon: Building2 },
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'stores', label: 'Stores', icon: StoreIcon },
];

export default function Organisations() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('organisations');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Organisations</h1>
          <p className="text-gray-500 mt-1">Manage accounts, users, stores and billing plans</p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries()}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border-2 border-gray-200 text-sm font-bold text-gray-700 hover:border-black hover:text-black transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-bold transition-all ${
              activeTab === id
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-500 hover:text-black'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'organisations' && <OrganisationsTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'stores' && <StoresTab />}
    </div>
  );
}
